import { FC, useEffect, useMemo, useState } from "react";
import { CarDataType } from "data/types";
import TabFilters from "./TabFilters";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import ExpandingFleetGrid from "designs/DesignB/components/FleetGrid/ExpandingFleetGrid";
import { useFleetFilters } from "hooks/useFleetFilters";
import FleetLayout, { FilterChip } from "designs/DesignB/components/Vehicles/FleetLayout";
import NoCarsMatch from "designs/DesignB/components/Vehicles/NoCarsMatch";
import { fleetCategories } from "designs/DesignB/components/CarLogo/categories";
import { FLEET_SORTS, FleetSort, sortFleet } from "utils/fleetSort";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { availableFleet, pickupLocation } = useData();
  // 1NOW-11228: the filters live in the URL, as on /vehicles (1NOW-11193): one class= per ticked
  // class, price=min-max and sort=. Going to a car and coming Back finds them there.
  const queryParams = new URLSearchParams(location.search);
  const classParams = (queryParams.getAll("class").length ? queryParams.getAll("class") : queryParams.getAll("make"))
    .map((name) => name.trim())
    .filter(Boolean);
  const urlSort = queryParams.get("sort");
  const urlPrice = (queryParams.get("price") ?? "").match(/^(\d+)-(\d+)$/);
  const urlRange = urlPrice && Number(urlPrice[1]) <= Number(urlPrice[2]) ? [Number(urlPrice[1]), Number(urlPrice[2])] : null;
  // 1NOW-11129: class and price are applied together, from one place.
  const {
    types,
    selectedTypes,
    setSelectedTypes,
    rangePrices,
    setRangePrices,
    maxPrice,
    filteredFleet,
    clearFilters,
    resetKey,
  } = useFleetFilters(availableFleet, classParams, urlRange);
  const [sort, setSort] = useState<FleetSort>(
    FLEET_SORTS.some((s) => s.value === urlSort) ? (urlSort as FleetSort) : "recommended"
  );
  // The cars the search left, in the order picked; how many of each class, for the sidebar.
  const sorted = useMemo(() => sortFleet(filteredFleet, sort), [filteredFleet, sort]);
  const counts = useMemo(
    () => Object.fromEntries(fleetCategories(availableFleet).map((c) => [c.name, c.count])),
    [availableFleet]
  );
  // The active filters, as chips the renter can take off one by one.
  const priceMoved = rangePrices[0] > 0 || rangePrices[1] < maxPrice;
  const chips: FilterChip[] = [
    ...selectedTypes.map((type) => ({
      key: `type-${type}`,
      label: type,
      onRemove: () => setSelectedTypes(selectedTypes.filter((t) => t !== type)),
    })),
    ...(priceMoved
      ? [{ key: "price", label: `$${rangePrices[0]} – $${rangePrices[1]}`, onRemove: () => setRangePrices([0, maxPrice]) }]
      : []),
  ];
  // 1NOW-11132: there are cars for these dates, and the filters leave none.
  const noMatches = (availableFleet?.length ?? 0) > 0 && filteredFleet.length === 0;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Every filter is written back, replaced, not pushed: Back leaves the page. The URL is not followed
  // the other way: a new search from the bar lands on a bare /available-car, and the filters on
  // screen stay, as they always have, and are written into it again. Other params are left alone.
  // Before the redirect below, so its trip home is the last word.
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    next.delete("make");
    next.delete("class");
    selectedTypes.forEach((type) => next.append("class", type));
    if (sort === "recommended") next.delete("sort");
    else next.set("sort", sort);
    if (priceMoved) next.set("price", `${rangePrices[0]}-${rangePrices[1]}`);
    else next.delete("price");
    const search = next.toString();
    if (search !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, selectedTypes, sort, priceMoved, rangePrices[0], rangePrices[1]]);

  // Opened with no search (a cold link or a reload): home, to start one. Only on arrival: the
  // renter editing the search bar on this page (clearing the pick-up, switching the return
  // location) stays on the results, as on Design A (1NOW-11153 reopen).
  useEffect(() => {
    if (!pickupLocation) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only active cars get a card, and the grid's row maths counts what it draws.
  const shown = useMemo(() => (sorted ?? []).filter((car: any) => car?.active === true), [sorted]);
  return (
    <div
      className={`nc-SectionGridFilterCard ${className}`}
      style={{ minHeight: "80vh" }}
      data-nc-id="SectionGridFilterCard"
    >
      <FleetLayout
        eyebrow="Your dates"
        title="Available fleet"
        count={filteredFleet?.length}
        sort={sort}
        onSort={setSort}
        chips={chips}
        onClearAll={clearFilters}
        filters={
          <TabFilters
            key={resetKey}
            types={types}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            rangePrices={rangePrices}
            setRangePrices={setRangePrices}
            maxPrice={maxPrice}
            counts={counts}
          />
        }
      >
        <div className="nc-SectionGridFeaturePlaces relative">
          {noMatches ? (
            <NoCarsMatch onClear={clearFilters} />
          ) : (
            // 1NOW-11219: a card opens the car's details in place; Book now in there goes on to
            // the booking page for these dates, as the card's own button used to.
            <ExpandingFleetGrid
              cars={shown}
              gridClassName="sm:grid-cols-2 xl:grid-cols-3"
              resetKey={`${selectedTypes.join("|")}/${rangePrices.join("-")}/${sort}`}
              onBook={() => navigate("/listing-car-detail")}
            />
          )}
        </div>
      </FleetLayout>
    </div>
  );
};

export default SectionGridFilterCard;
